/**
 * SeededRandom.ts
 * Deterministic PRNG using Mulberry32 algorithm.
 * Same seed always produces the same sequence.
 * Pattern borrowed from Landscaper for consistency.
 */

export class SeededRandom {
    private state: number

    constructor(seed: number) {
        this.state = seed | 0
    }

    /** Returns a float in [0, 1) */
    next(): number {
        this.state |= 0
        this.state = this.state + 0x6D2B79F5 | 0
        let t = Math.imul(this.state ^ this.state >>> 15, 1 | this.state)
        t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t
        return ((t ^ t >>> 14) >>> 0) / 4294967296
    }

    /** Returns an integer in [min, max] inclusive */
    intRange(min: number, max: number): number {
        return Math.floor(this.next() * (max - min + 1)) + min
    }

    /** Returns a float in [min, max) */
    floatRange(min: number, max: number): number {
        return this.next() * (max - min) + min
    }

    /** Returns true with given probability [0, 1] */
    chance(probability: number): boolean {
        return this.next() < probability
    }

    /** Picks a random element from an array */
    pick<T>(array: T[]): T {
        return array[Math.floor(this.next() * array.length)]
    }

    /** Shuffles an array in place (Fisher-Yates) */
    shuffle<T>(array: T[]): T[] {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(this.next() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]]
        }
        return array
    }

    /** Picks a key from a weighted map. Higher weight = more likely. */
    weightedPick<K extends string>(weights: Partial<Record<K, number>>): K {
        const entries = Object.entries(weights).filter(([, w]) => (w as number) > 0) as [K, number][]
        const total = entries.reduce((sum, [, w]) => sum + w, 0)
        let roll = this.next() * total
        for (const [key, weight] of entries) {
            roll -= weight
            if (roll <= 0) return key
        }
        return entries[entries.length - 1][0]
    }

    /** Creates a derived seed for sub-systems */
    derive(label: string): number {
        let hash = 0
        for (let i = 0; i < label.length; i++) {
            hash = ((hash << 5) - hash + label.charCodeAt(i)) | 0
        }
        return (this.state ^ hash) >>> 0
    }
}
